import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import ExecutionHistoryHeader from "./history/ExecutionHistoryHeader";
import ExecutionHistoryStats from "./history/ExecutionHistoryStats";
import ExecutionHistoryFilters from "./history/ExecutionHistoryFilters";
import ExecutionHistoryTable from "./history/ExecutionHistoryTable";
import { ExecutionLogsModal } from "./modals/ExecutionLogsModal";
import { notify } from "../../../../services/toast.config";
import {
  selectActiveCompany,
  selectCurrentExecution,
} from "../../../../core/data/redux/automations/selector";
import {
  fetchExecutionHistory,
  fetchExecutionDetails,
} from "../../../../core/data/redux/automations/thunks";
import { clearCurrentExecution } from "../../../../core/data/redux/automations/slice";

import type { AppDispatch } from "../../../../core/data/redux/store";
import type {
  ExecutionData,
  ExecutionPagination,
  ExecutionStats,
} from "../types/automation.types";

const DEFAULT_PAGINATION: ExecutionPagination = {
  page: 1,
  limit: 25,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
};

const ExecutionHistory = () => {
  const { id } = useParams<{ id: string }>();
  const workflowId = parseInt(id || "0");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const activeCompany = useSelector(selectActiveCompany);
  const currentExecution = useSelector(selectCurrentExecution);

  const title: string = location?.state?.title || "Execution History";
  const breadcrumb: string = location?.state?.breadcrumb || "Execution History";
  const backRoute: string = location?.state?.backRoute || "/automation";
  const dashboardRoute: string = location?.state?.dashboardRoute || "/dashboard";
  const callingBreadcrumb: string =
    location?.state?.callingBreadcrumb || "Workflows";

  const [executions, setExecutions] = useState<ExecutionData[]>([]);
  const [pagination, setPagination] =
    useState<ExecutionPagination>(DEFAULT_PAGINATION);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedExecution, setSelectedExecution] = useState<ExecutionData | null>(
    null,
  );
  const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const detailRequestRef = useRef(0);

  const activeFiltersCount = [statusFilter, startDate || endDate].filter(
    Boolean,
  ).length;

  const filteredExecutions = useMemo(() => {
    let filtered = [...executions];

    if (statusFilter) {
      filtered = filtered.filter((exec) => exec.status === statusFilter);
    }

    if (startDate && endDate) {
      filtered = filtered.filter((exec) => {
        const startedDay = dayjs(exec.started_at).format("YYYY-MM-DD");
        return startedDay >= startDate && startedDay <= endDate;
      });
    }

    return filtered;
  }, [executions, statusFilter, startDate, endDate]);

  const summaryStats: ExecutionStats = useMemo(
    () => ({
      total: pagination.totalRecords,
      completed: executions.filter((e) => e.status === "completed").length,
      failed: executions.filter((e) => e.status === "failed").length,
      running: executions.filter((e) => e.status === "running").length,
    }),
    [executions, pagination.totalRecords],
  );

  const loadData = async (page: number = 1, pageSize: number = 25) => {
    if (!activeCompany?.company_id || !workflowId) {
      setError(
        !activeCompany?.company_id
          ? "Please select a company first"
          : "Invalid workflow ID",
      );
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await dispatch(
        fetchExecutionHistory({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          workflowId,
          page,
          limit: pageSize,
        }),
      ).unwrap();

      setExecutions(result?.data || []);
      setPagination(result?.pagination || DEFAULT_PAGINATION);
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to load execution history";
      setError(errorMsg);
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(pagination.page, pagination.limit);
  }, [activeCompany?.company_id, activeCompany?.company_type, workflowId]);

  const handleDateChange = (start: string | null, end: string | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleReset = () => {
    setStatusFilter(null);
    setStartDate(null);
    setEndDate(null);
  };

  const handleViewLogs = (record: ExecutionData) => {
    dispatch(clearCurrentExecution());
    setSelectedExecution(record);
    setIsLogsModalVisible(true);
    if (activeCompany?.company_id) {
      const requestId = ++detailRequestRef.current;
      setDetailsLoading(true);
      dispatch(
        fetchExecutionDetails({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          executionId: record.id,
        }),
      ).finally(() => {
        if (detailRequestRef.current === requestId) {
          setDetailsLoading(false);
        }
      });
    }
  };

  const handleCloseLogs = () => {
    detailRequestRef.current += 1;
    setIsLogsModalVisible(false);
    setSelectedExecution(null);
    setDetailsLoading(false);
    dispatch(clearCurrentExecution());
  };

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Execution History</h4>
            <p>{error}</p>
            <hr />
            <div className="d-flex gap-2">
              <button
                onClick={() => navigate(backRoute)}
                className="btn btn-primary"
              >
                <i className="ti ti-arrow-left me-2" />
                Back to Workflows
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-secondary"
              >
                <i className="ti ti-reload me-2" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <ExecutionHistoryHeader
            title={title}
            breadcrumb={breadcrumb}
            callingBreadcrumb={callingBreadcrumb}
            dashboardRoute={dashboardRoute}
            backRoute={backRoute}
          />

          <ExecutionHistoryStats stats={summaryStats} loading={loading} />

          <ExecutionHistoryFilters
            statusFilter={statusFilter}
            startDate={startDate}
            endDate={endDate}
            activeFiltersCount={activeFiltersCount}
            onStatusChange={setStatusFilter}
            onDateChange={handleDateChange}
            onReset={handleReset}
          />

          <ExecutionHistoryTable
            executions={filteredExecutions}
            loading={loading}
            pagination={pagination}
            onPageChange={(page) => loadData(page, pagination.limit)}
            onLimitChange={(limit) => loadData(1, limit)}
            onViewLogs={handleViewLogs}
          />
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </div>

        <ExecutionLogsModal
          visible={isLogsModalVisible}
          onClose={handleCloseLogs}
          execution={selectedExecution as any}
          details={currentExecution as any}
          loadingDetails={detailsLoading}
        />
      </div>
    </>
  );
};

export default ExecutionHistory;
